import * as acorn from 'acorn'

/**
 * コードスニペットをハイライト表示 (ファイルスコープのヘルパー)
 */
const highlightCodeInternal = (code: string, errorLine: number, errorColumn?: number) => {
  const lines = code.split('\n')
  const contextSize = 2
  const startLine = Math.max(0, errorLine - contextSize - 1)
  const endLine = Math.min(lines.length, errorLine + contextSize)
  let highlighted = ''
  for (let i = startLine; i < endLine; i++) {
    const lineNum = i + 1
    const isErrorLine = lineNum === errorLine
    const prefix = isErrorLine ? '>>> ' : '    '
    const lineNumStr = String(lineNum).padStart(String(lines.length).length, ' ')
    highlighted += `${lineNumStr}: ${prefix}${lines[i] || ''}\n`
    if (isErrorLine && errorColumn !== undefined && errorColumn >= 0) {
      const displayLineNumberLength = String(lines.length).length
      const displayPrefixLength = prefix.length
      const padding = ' '.repeat(
        displayLineNumberLength + 2 + displayPrefixLength + errorColumn,
      )
      highlighted += `${padding}^ エラー位置\n`
    }
  }
  return highlighted
}

export interface ExecuteStringDefinedFunctionOptions {
  functionString: string
  functionArgValues?: { [argName: string]: any }
  errorThrow?: boolean
  functionArgExpression?: string
  errorMessagePrefix?: string
  successCallback?: (data: any) => void | Promise<void>
  bindingThisObject?: any
  returnAsExecutableFunction?: boolean
  quiet?: boolean
}

class StringFunctionExecutorService {
  private cache = new Map<string, { func?: Function; errorMessage?: string }>()

  constructor() {
    // Singleton-like behavior if instantiated once at module level
  }

  private async _formatErrorMessage(
    error: Error,
    phase: 'syntax' | 'runtime',
    functionString: string,
    errorMessagePrefix: string,
    quiet: boolean,
    preciseLocation?: { line: number; column: number },
  ): Promise<string> {
    let enhancedMessage = `${errorMessagePrefix}${phase === 'syntax' ? '構文' : '実行時'}エラー: ${error.message}\n`
    const linesInUserCode = functionString.split('\n').length
    let positionFound = false

    if (
      preciseLocation &&
      preciseLocation.line > 0 &&
      preciseLocation.line <= linesInUserCode
    ) {
      enhancedMessage += '\nエラー位置の詳細 (パーサーより):\n'
      enhancedMessage += '```js' + '\n'
      enhancedMessage += highlightCodeInternal(
        functionString,
        preciseLocation.line,
        preciseLocation.column,
      )
      enhancedMessage += '```' + '\n'
      enhancedMessage += `位置: ${preciseLocation.line}行目, ${preciseLocation.column + 1}列目\n`
      positionFound = true
    }

    if (!positionFound) {
      if (!quiet) {
        console.log(
          '[_formatErrorMessage] No precise position, trying message/stack parsing.',
        )
      }
      const regexes = [
        /line\s*(\d+)(?::(\d+))?/i,
        /<anonymous>:(\d+):(\d+)/,
        /at eval.+<anonymous>:(\d+):(\d+)/,
      ]
      let lineMatch: RegExpMatchArray | null = null
      for (const regex of regexes) {
        lineMatch =
          error.message.match(regex) || (error.stack ? error.stack.match(regex) : null)
        if (lineMatch) break
      }
      if (lineMatch && lineMatch[1]) {
        const errorLine = parseInt(lineMatch[1])
        const errorColumn = lineMatch[2] ? parseInt(lineMatch[2]) - 1 : undefined
        if (errorLine > 0 && errorLine <= linesInUserCode) {
          enhancedMessage += '\nエラー位置の詳細 (メッセージ/スタックから抽出):\n'
          enhancedMessage += '```js' + '\n'
          enhancedMessage += highlightCodeInternal(functionString, errorLine, errorColumn)
          enhancedMessage += '```' + '\n'
          enhancedMessage += `位置: ${errorLine}行目`
          if (errorColumn !== undefined)
            enhancedMessage += `, ${errorColumn + 1}列目 (推定)`
          enhancedMessage += '\n'
        }
      }
    }
    if (!quiet) console.log('--- DEBUG END [_formatErrorMessage] ---')
    return enhancedMessage
  }

  public async execute(
    options: ExecuteStringDefinedFunctionOptions,
  ): Promise<any | Function | null> {
    const {
      functionString,
      functionArgValues,
      errorThrow = false,
      functionArgExpression = '',
      errorMessagePrefix = '',
      successCallback = null,
      bindingThisObject = null,
      returnAsExecutableFunction = false,
      quiet = false,
    } = options
    if (
      !functionString ||
      typeof functionString !== 'string' ||
      functionString.trim() === ''
    ) {
      const errorMessage = `${errorMessagePrefix}関数の文字列が無効です。`
      // 現在の runtime から call stack を取得
      const currentStack = (new Error().stack || '').split('at ')
      if (!quiet) {
        console.error(`[executeStringDefinedFunction]`, errorMessage, {
          functionArgValues,
          functionArgExpression,
          currentStack,
        })
      }
      if (errorThrow) throw new Error(errorMessage)
      return null
    }

    const cacheKey = functionString

    if (this.cache.has(cacheKey)) {
      const cachedItem = this.cache.get(cacheKey)!
      // if (!quiet)
      //   console.log('[Service.execute] Cache hit for:', cacheKey.substring(0, 50) + '...')
      if (cachedItem.errorMessage) {
        if (!quiet) console.error(cachedItem.errorMessage)
        if (errorThrow) throw new Error(cachedItem.errorMessage)
        return returnAsExecutableFunction ? null : Promise.resolve(null) // Adjusted for sync return
      }
      if (cachedItem.func) {
        const cachedFunc = cachedItem.func
        if (returnAsExecutableFunction) {
          return async (...args: any[]) => {
            // This wrapper is async, as the original func is async
            try {
              return await cachedFunc.call(bindingThisObject, ...args)
            } catch (runtimeError: any) {
              const message = await this._formatErrorMessage(
                runtimeError,
                'runtime',
                functionString,
                errorMessagePrefix,
                quiet,
              )
              if (!quiet) console.error(message)
              if (errorThrow) {
                runtimeError.message = message
                throw runtimeError
              }
              return null
            }
          }
        }
        try {
          const result = await cachedFunc.call(bindingThisObject, functionArgValues)
          if (successCallback) {
            const cbResult = successCallback(result)
            if (cbResult instanceof Promise) await cbResult
          }
          return result
        } catch (runtimeError: any) {
          const message = await this._formatErrorMessage(
            runtimeError,
            'runtime',
            functionString,
            errorMessagePrefix,
            quiet,
          )
          if (!quiet) console.error(message)
          if (errorThrow) {
            runtimeError.message = message
            throw runtimeError
          }
          return null
        }
      }
    }
    // if (!quiet) {
    //   console.log('[Service.execute] Cache miss for:', cacheKey.substring(0, 50) + '...')
    // }
    const functionArg =
      functionArgExpression || `{${Object.keys(functionArgValues || {}).join(', ')}}`
    const functionTemplate = `return async function (${functionArg}) { ${functionString} }`

    try {
      const func = new Function(functionTemplate)()
      this.cache.set(cacheKey, { func })
      // if (!quiet) {
      //   console.log('[Service.execute] Function compiled and cached.')
      // }

      if (returnAsExecutableFunction) {
        // Return the raw compiled function (or a wrapper if needed for consistent async behavior)
        // The compiled func is already async due to `async function (${functionArg})`
        return func // Directly return the function
      }

      const result = await func.call(bindingThisObject, functionArgValues)
      if (successCallback) {
        const cbResult = successCallback(result)
        if (cbResult instanceof Promise) await cbResult
      }
      return result
    } catch (compilationOrRuntimeError: any) {
      try {
        acorn.parse(functionTemplate, {
          ecmaVersion: 'latest',
          locations: true,
          allowReturnOutsideFunction: true,
        })
        if (!quiet) {
          console.log('[Service.execute] Acorn parsing successful.')
        }
      } catch (syntaxError: any) {
        if (!quiet)
          console.error('[Service.execute] Acorn caught syntax error:', syntaxError)
        const preciseLocation = syntaxError.loc
          ? {
              line: syntaxError.loc.line,
              column: syntaxError.loc.column,
            }
          : undefined
        const formattedMsg = await this._formatErrorMessage(
          syntaxError,
          'syntax',
          functionTemplate,
          errorMessagePrefix,
          quiet,
          preciseLocation,
        )
        this.cache.set(cacheKey, { errorMessage: formattedMsg })
        if (!quiet) console.error(formattedMsg)
        if (errorThrow) throw new Error(formattedMsg)
        return returnAsExecutableFunction ? null : Promise.resolve(null) // Adjusted for sync return
      }
      if (!quiet)
        console.error(
          '[Service.execute] Error during new Function() or its execution:',
          compilationOrRuntimeError,
        )
      const phase =
        compilationOrRuntimeError instanceof SyntaxError ? 'syntax' : 'runtime'
      const preciseLocation = compilationOrRuntimeError.loc
        ? {
            line: compilationOrRuntimeError.loc.line,
            column: compilationOrRuntimeError.loc.column,
          }
        : undefined
      const formattedMsg = await this._formatErrorMessage(
        compilationOrRuntimeError,
        phase,
        functionString,
        errorMessagePrefix,
        quiet,
        preciseLocation,
      )
      this.cache.set(cacheKey, { errorMessage: formattedMsg })
      if (!quiet) console.error(formattedMsg)
      if (errorThrow) throw new Error(formattedMsg)
      return returnAsExecutableFunction ? null : Promise.resolve(null) // Adjusted for sync return
    }
  }
}

// Create a single instance of the service
const executorServiceInstance = new StringFunctionExecutorService()

/**
 * 文字列定義された関数を実行
 */
export const executeStringDefinedFunction = (
  options: ExecuteStringDefinedFunctionOptions,
): Promise<any | Function | null> => {
  return executorServiceInstance.execute(options)
}
